import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData, type QueryClient } from "@tanstack/react-query";
import {
  deletePost, deletePostV2, getWall, isTypedPost, likePost, searchRefusalOf, unlikePost,
  type ContentError, type Page, type WallCounts, type WallFeedSource, type WallLike, type WallPost,
} from "../api/content";
import { useWallPlaybackStore } from "../store/wallPlaybackStore";
import { retryUnlessStaleCursor, useRestartOnStaleCursor } from "./staleCursor";

type Feed = InfiniteData<Page<WallPost>, string | null>;

/** One cache per feed source (spec 3b §4.2). A user id never shares a key with an equal GC id. */
export const wallFeedKey = (s: WallFeedSource) =>
  [
    "wallFeed",
    s.type,
    s.type === "gc" ? s.convId
      : s.type === "user" ? s.userId
      : s.type === "gcSearch" ? `${s.convId}:${s.q}`
      : s.type === "world" ? `${s.postType}:${s.onMaptxat ? "maptxat" : "all"}`
      : s.type === "worldSearch" ? `${s.postType ?? "ALL"}:${s.q}`
      : null,
  ] as const;

const WALL_FEED_OFF = ["wallFeed", "off", null] as const;

/**
 * World search results are a snapshot: no refetch on mount or window focus, fresh for a minute (room-stc allows
 * 60 searches a minute per user). Likes, edits and deletes still reach them through mapPost / removePost.
 */
const SEARCH_STALE_MS = 60_000;

export function useWallFeed(source: WallFeedSource | null) {
  const qc = useQueryClient();
  const key = source ? wallFeedKey(source) : WALL_FEED_OFF;
  const retry = retryUnlessStaleCursor(qc);
  const search = source?.type === "worldSearch";
  const feed = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => getWall(source as WallFeedSource, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<WallPost>) => last.nextCursor ?? undefined,
    enabled: source != null,
    // A rate-limited or unavailable World search is shown at once; retrying would only spend more of the limit.
    retry: search ? (n: number, e: Error) => searchRefusalOf(e) == null && retry(n, e) : retry,
    ...(search ? { staleTime: SEARCH_STALE_MS, refetchOnMount: false, refetchOnWindowFocus: false } : {}),
  });
  useRestartOnStaleCursor(feed, key);
  return feed;
}

/**
 * Personal feeds (users/{id}, users/me, feed/following) get no realtime events (spec 1):
 * they refresh after the user's own writes and when a post dialog closes. World search results are left alone: each
 * refetch re-runs every loaded page against the search rate limit and can reorder the list under the reader.
 */
export function invalidatePersonalFeeds(qc: QueryClient) {
  return qc.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === "wallFeed" && q.queryKey[1] !== "gc" && q.queryKey[1] !== "off" && q.queryKey[1] !== "worldSearch",
  });
}

/** After a News post: the General Wall and the user's post lists / counts. One helper for the direct and queued save paths. */
export function invalidateNewsPosted(qc: QueryClient) {
  void invalidatePersonalFeeds(qc);
  void qc.invalidateQueries({ queryKey: ["personalPosts"] });
  void qc.invalidateQueries({ queryKey: ["typeCounts"] });
}

function mapFeed(qc: QueryClient, source: WallFeedSource, fn: (pages: Page<WallPost>[]) => Page<WallPost>[]) {
  qc.setQueryData<Feed>(wallFeedKey(source), (old) => (old ? { ...old, pages: fn(old.pages) } : old));
}

const ALL_FEEDS = ["wallFeed"] as const;
/** Post detail entries (useWallPost, keyed by ULID): every post write reaches them too. */
const ALL_DETAILS = ["wallPost"] as const;

/** The Books tab's Library column is a feed of posts too (not under "wallFeed"): likes reach it as well. */
const LIBRARY = ["library"] as const;

const holdsPost = (feed: Feed | undefined, postId: string) =>
  !!feed?.pages.some((pg) => pg.items.some((p) => p.id === postId));

function mapDetails(qc: QueryClient, postId: string, fn: (p: WallPost) => WallPost) {
  qc.setQueriesData<WallPost>({ queryKey: ALL_DETAILS }, (old) => (old?.id === postId ? fn(old) : old));
}

/**
 * The same post can sit in several cached feeds (gc, me, user/N, following) and in an open post dialog:
 * update every one that holds it.
 */
function mapPost(qc: QueryClient, postId: string, fn: (p: WallPost) => WallPost) {
  for (const queryKey of [ALL_FEEDS, LIBRARY]) {
    qc.setQueriesData<Feed>({ queryKey }, (old) =>
      holdsPost(old, postId)
        ? { ...old!, pages: old!.pages.map((pg) => ({ ...pg, items: pg.items.map((p) => (p.id === postId ? fn(p) : p)) })) }
        : old,
    );
  }
  mapDetails(qc, postId, fn);
}

export function upsertPost(qc: QueryClient, source: WallFeedSource, post: WallPost) {
  mapDetails(qc, post.id, (p) => ({ ...post, likedByMe: post.likedByMe ?? p.likedByMe }));
  mapFeed(qc, source, (pages) => {
    const exists = pages.some((pg) => pg.items.some((p) => p.id === post.id));
    if (exists) {
      return pages.map((pg) => ({
        ...pg,
        items: pg.items.map((p) => (p.id === post.id ? { ...post, likedByMe: post.likedByMe ?? p.likedByMe } : p)),
      }));
    }
    if (pages.length === 0) return pages;
    const [first, ...rest] = pages;
    return [{ ...first, items: [{ ...post, likedByMe: post.likedByMe ?? false }, ...first.items] }, ...rest];
  });
}

/** Replace a post only if it is already loaded; never inserts. */
export function replacePost(qc: QueryClient, post: WallPost) {
  mapPost(qc, post.id, (p) => ({ ...post, likedByMe: post.likedByMe ?? p.likedByMe }));
}

export function removePost(qc: QueryClient, postId: string) {
  // Every delete (mine, or WALL_POST_DELETED) lands here: the dock stops playing a post that is gone.
  useWallPlaybackStore.getState().stopPost(postId);
  qc.setQueriesData<Feed>({ queryKey: ALL_FEEDS }, (old) =>
    holdsPost(old, postId)
      ? { ...old!, pages: old!.pages.map((pg) => ({ ...pg, items: pg.items.filter((p) => p.id !== postId) })) }
      : old,
  );
  // A detail entry on screen stays (its dialog is closing, or the post went away under it);
  // one no one shows is dropped so reopening the post cannot paint the deleted copy.
  qc.removeQueries({ queryKey: ALL_DETAILS, type: "inactive", predicate: (q) => (q.state.data as WallPost | undefined)?.id === postId });
}

export function patchCounts(qc: QueryClient, c: WallCounts) {
  mapPost(qc, c.postId, (p) => ({ ...p, likeCount: c.likeCount, commentCount: c.commentCount, captionFallback: c.captionFallback }));
}

/** `source` is the feed the like comes from (its in-flight fetch is cancelled); none from a post dialog. */
export function useLikePost(source?: WallFeedSource) {
  const qc = useQueryClient();
  type Snap = [readonly unknown[], Feed | WallPost | undefined][];
  return useMutation<WallLike, ContentError, WallPost, { prev: Snap }>({
    mutationFn: (post) => (post.likedByMe ? unlikePost(post.id) : likePost(post.id)),
    onMutate: async (post) => {
      if (source) await qc.cancelQueries({ queryKey: wallFeedKey(source) });
      // Snapshot every feed and detail entry that holds the post, so a failed like restores all of them.
      const prev: Snap = [
        ...qc.getQueriesData<Feed>({ queryKey: ALL_FEEDS }).filter(([, d]) => holdsPost(d, post.id)),
        ...qc.getQueriesData<Feed>({ queryKey: LIBRARY }).filter(([, d]) => holdsPost(d, post.id)),
        ...qc.getQueriesData<WallPost>({ queryKey: ALL_DETAILS }).filter(([, d]) => d?.id === post.id),
      ];
      const liked = !post.likedByMe;
      mapPost(qc, post.id, (p) => ({ ...p, likedByMe: liked, likeCount: Math.max(0, p.likeCount + (liked ? 1 : -1)) }));
      return { prev };
    },
    onError: (_e, _post, ctx) => {
      for (const [key, data] of ctx?.prev ?? []) qc.setQueryData(key, data);
    },
    onSuccess: (res, post) => {
      mapPost(qc, post.id, (p) => ({ ...p, likedByMe: res.likedByMe, likeCount: res.likeCount }));
      // A new like appears in the Liked list the next time it is shown; after an unlike the refetch replaces
      // any Liked page that a refresh sent before the unlike landed wrote back as still liked.
      qc.invalidateQueries({ queryKey: wallFeedKey({ type: "liked" }) });
    },
  });
}

/** The post to delete: typed (non-WALL) posts go through the v2 route, since v1 post routes are WALL-only. */
export type DeleteTarget = { id: string; type?: string | null };

export function useDeletePost() {
  const qc = useQueryClient();
  return useMutation<void, ContentError, DeleteTarget>({
    mutationFn: (p) => (isTypedPost(p) ? deletePostV2(p.id) : deletePost(p.id)),
    onSuccess: (_r, p) => {
      // Gone from every feed (the events lists and agendas are "wallFeed" too); the personal page counts change.
      removePost(qc, p.id);
      void qc.invalidateQueries({ queryKey: ["typeCounts"] });
      // A deleted offer leaves the balance card's "nights offered" (apopilo/me stats) and the requests list.
      if (p.type === "APOPILO") {
        void qc.invalidateQueries({ queryKey: ["apopiloMe"] });
        void qc.invalidateQueries({ queryKey: ["apopiloRequests"] });
      }
    },
  });
}
